/* eslint-disable no-restricted-globals */
import { Suspense } from 'react';
import GlobalStyles from './GlobalStyles';
import AppBar from '../../components/AppBar';
import loadTranslations from '../../translations/loadTranslations';
import { ConfigProvider } from 'antd';
import ReactGA from 'react-ga4';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import Footer from '../../components/Footer';
import { useIsMobile } from '../../utils/hooks';
import { useAnimalHeaven } from '../../context/animalHeaven.provider';
import { getInitialState } from '../../utils/actions';
import {
  addLocationToStack,
  setCurrency,
  setInitialState,
} from '../../context/animalHeaven.actions';
import useApi from '../../utils/api';
import { FloatButton } from 'antd';
import useStyle from './index.styles';
import { Plus, XMarkSolid, Home as HomeIcon, HeartBeat } from '../../utils/icons';
import { colors } from '../../utils/theme';
import Gif from '../../components/Gif';
import {
  getNamePathToPath,
  getTranslation,
  hideGlobalSpinner,
  showGlobalSpinner,
} from '../../utils';
import ChangeDate from '../../components/ChangeDate';
import Loader from '../../components/Loader';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import routes, { RouteType } from './routes';
import axios from 'axios';

import './App.css';

ReactGA.initialize([{ trackingId: process.env.REACT_APP_GA4_MEASUREMENT_ID as string }]);

const helmetContext = {};

const ProtectedRouteWithToken = ({ children }: { children: ReactElement }) => {
  const { token } = useAnimalHeaven();
  if (token) {
    return children;
  }

  return <Navigate to="/" replace />;
};

const App = () => {
  const [appBarHeight, setAppBarHeight] = useState<number>(0);
  const [isLoadingInitialState, setIsLoadingInitialState] = useState<boolean>(true);
  const [isOpenFloatButtonOptions, setIsOpenFloatButtonOptions] = useState<boolean>(false);
  const appBarRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const currentPage = useLocation().pathname;
  const isMobile = useIsMobile();
  const {
    animalHeavenDispatch,
    token: tokenInContext,
    user: { id: userId, language },
  } = useAnimalHeaven();
  const { api } = useApi();
  const classes = useStyle();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const termsPath = getNamePathToPath('terms').split('?')[0];

  useEffect(() => {
    const loadTranslationsAndRemoveSpinner = async () => {
      await loadTranslations();
      hideGlobalSpinner();
      setAppBarHeight(appBarRef.current?.clientHeight || 0);
    };
    if (!isLoadingInitialState) {
      loadTranslationsAndRemoveSpinner();
    }
  }, [isLoadingInitialState]);

  useEffect(() => {
    const token = localStorage.getItem('animal_heaven_token');

    if (!userId && token) {
      api(getInitialState())
        .then((resp) => {
          if (resp.status === 200) {
            animalHeavenDispatch(setInitialState(resp.data));
          }
        })
        .finally(() => setIsLoadingInitialState(false));
    } else {
      setIsLoadingInitialState(false);
    }
  }, [animalHeavenDispatch, api, userId, tokenInContext]);

  useEffect(() => {
    const getCurrentCountry = async () => {
      axios.get(`${process.env.REACT_APP_API_URL}ipInformation`).then((resp: any) => {
        if (resp.data.currency) {
          animalHeavenDispatch(setCurrency(resp.data.currency));
        }
      });
    };

    if (!isLoadingInitialState) {
      if (currentPage !== termsPath) {
        getCurrentCountry();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animalHeavenDispatch, isLoadingInitialState]);

  useEffect(() => {
    const onResize = () => {
      setAppBarHeight((prevState) =>
        appBarRef.current?.clientHeight !== prevState
          ? appBarRef.current?.clientHeight || 0
          : prevState
      );
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    bodyRef?.current?.scrollIntoView({ behavior: 'smooth' });
    animalHeavenDispatch(addLocationToStack(currentPage));
    ReactGA.send({ hitType: 'pageview', page: location.pathname, title: 'Pageview' });
  }, [animalHeavenDispatch, currentPage, t]);

  useEffect(() => {
    const setAndLoadLanguage = async () => {
      const isLanguageLoaded = i18n.hasResourceBundle(language, 'translation');
      if (!isLanguageLoaded) {
        showGlobalSpinner();
        const translation = await getTranslation(language);
        i18n.addResourceBundle(language, 'translation', translation);
        hideGlobalSpinner();
      }
      i18n.changeLanguage(language);
      dayjs.locale(language.slice(0, 2));
    };

    setAndLoadLanguage();
  }, [i18n, language]);

  const minHeight = `calc(100vh - ${appBarHeight}px)`;

  const canShowFooter = () => {
    if (
      currentPage === getNamePathToPath('admin') ||
      currentPage === getNamePathToPath('last-animals-in-heaven') ||
      currentPage === termsPath
    ) {
      return false;
    }

    if (isMobile) {
      if (currentPage.includes('/animals-in-heaven')) {
        return true;
      }
    } else {
      return true;
    }

    return false;
  };

  const showFloatButton = !!(
    userId &&
    isMobile &&
    (currentPage === getNamePathToPath('mission') ||
      currentPage.includes('/animals-in-heaven') ||
      currentPage.includes('/adoption-centers') ||
      currentPage.includes('/my-memorials'))
  );

  return (
    <HelmetProvider context={helmetContext}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: colors.primary,
          },
        }}
      >
        <GlobalStyles />

        {!isLoadingInitialState && (
          <div className="flex h-screen flex-col">
            <div ref={appBarRef} className="fixed z-10 w-full bg-bg4">
              {currentPage !== getNamePathToPath('admin') &&
                currentPage !== getNamePathToPath('last-animals-in-heaven') &&
                currentPage !== termsPath && <AppBar />}
            </div>
            <div
              className="flex flex-col overflow-auto"
              style={{
                marginTop: `${currentPage === getNamePathToPath('admin') || currentPage === termsPath ? 0 : appBarHeight}px`,
              }}
            >
              <div className="flex" ref={bodyRef}>
                <Suspense
                  fallback={
                    <div style={{ minHeight }} className="flex w-full items-center justify-center">
                      <Loader />
                    </div>
                  }
                >
                  <Routes>
                    {routes.map(({ Component, isProtected, path }: RouteType) => (
                      <Route
                        key={path}
                        path={path}
                        element={
                          isProtected ? (
                            <>
                              <Helmet>
                                <title>{t('helmet.default.title')}</title>
                              </Helmet>
                              <ProtectedRouteWithToken>
                                <Component minHeight={minHeight} />
                              </ProtectedRouteWithToken>
                            </>
                          ) : (
                            <Component minHeight={minHeight} />
                          )
                        }
                      />
                    ))}
                  </Routes>
                </Suspense>
              </div>
              {canShowFooter() && <Footer />}
            </div>
            {isOpenFloatButtonOptions && (
              <div className="top fixed z-20 h-screen w-screen bg-[#ffffff99]" />
            )}
            {showFloatButton && (
              <FloatButton.Group
                trigger="click"
                type="primary"
                icon={<Plus onClick={() => setIsOpenFloatButtonOptions(true)} />}
                className={classes.floatButton}
                closeIcon={<XMarkSolid onClick={() => setIsOpenFloatButtonOptions(false)} />}
                open={isOpenFloatButtonOptions}
              >
                <FloatButton
                  icon={
                    <div className="flex [&>svg>g>path]:fill-bg4 [&>svg]:w-[18px]">
                      <HomeIcon
                        onClick={() => {
                          setIsOpenFloatButtonOptions(false);
                          navigate('/animal-adoption-form');
                        }}
                      />
                    </div>
                  }
                />
                <FloatButton
                  icon={
                    <div className="flex [&>svg>path]:fill-bg4">
                      <HeartBeat
                        onClick={() => {
                          setIsOpenFloatButtonOptions(false);
                          navigate('/add-memorial');
                        }}
                      />
                    </div>
                  }
                />
              </FloatButton.Group>
            )}
            <Gif />
            {currentPage !== getNamePathToPath('last-animals-in-heaven') && <ChangeDate />}
          </div>
        )}
      </ConfigProvider>
    </HelmetProvider>
  );
};

export default App;
